import moment from 'moment';
import { compose, Action as ReduxAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { SiteCheckpointId } from '@/modules/common/model';
import { ValidateActionMap } from '@/shared/modules/redux/model';

import type { FormContent } from '../components/Form';
import type { State } from '../reducers';
import { getArrivalSites, getSiteWithId } from '../reducers/sites';
import { estimateBooking } from './estimate';
import { fetchServices } from './service';
import { filterArrivalSites } from './site';
import { clearArrivalSlots, clearDepartureSlots, fetchArrivalSlots, fetchDepartureSlots } from './slot';
import { Action as SlotAction } from './slot';
import { clearTripInfos, Action as TripAction } from './trip';

export namespace Action {
  export enum Type {
    DEPARTURE_CHANGED = 'DEPARTURE_CHANGED',
    ARRIVAL_CHANGED = 'ARRIVAL_CHANGED',
    INVALID_ARRIVAL_TRIP = 'INVALID_ARRIVAL_TRIP',
    DEPARTURE_CHECKPOINT_CHANGED = 'DEPARTURE_CHECKPOINT_CHANGED',
    ARRIVAL_CHECKPOINT_CHANGED = 'ARRIVAL_CHECKPOINT_CHANGED',
  }

  export interface DepartureChangedAction extends ReduxAction<Type.DEPARTURE_CHANGED> {
    departure: FormContent;
  }

  export interface ArrivalChangedAction extends ReduxAction<Type.ARRIVAL_CHANGED> {
    arrival: FormContent;
  }

  export interface DepartureCheckpointChangedAction extends ReduxAction<Type.DEPARTURE_CHECKPOINT_CHANGED> {
    checkpoint: SiteCheckpointId;
  }

  export interface ArrivalCheckpointChangedAction extends ReduxAction<Type.ARRIVAL_CHECKPOINT_CHANGED> {
    checkpoint: SiteCheckpointId;
  }
}

export type Action<T extends Action.Type = Action.Type> = ValidateActionMap<{
  [Action.Type.DEPARTURE_CHANGED]: Action.DepartureChangedAction;
  [Action.Type.ARRIVAL_CHANGED]: Action.ArrivalChangedAction;
  [Action.Type.INVALID_ARRIVAL_TRIP]: ReduxAction<Action.Type.INVALID_ARRIVAL_TRIP>;
  [Action.Type.DEPARTURE_CHECKPOINT_CHANGED]: Action.DepartureCheckpointChangedAction;
  [Action.Type.ARRIVAL_CHECKPOINT_CHANGED]: Action.ArrivalCheckpointChangedAction;
}>[T];

type AppThunkDispatch = ThunkDispatch<State, unknown, Action | SlotAction | TripAction>;

const fetchDepartureSlotsIfRequired =
  (before: FormContent, after: FormContent) =>
  (dispatch: AppThunkDispatch): void => {
    if (after.site && after.date) {
      if (before.site !== after.site || before.date !== after.date) {
        dispatch(fetchDepartureSlots(after.site, after.date));
      }
    } else dispatch(clearDepartureSlots());
  };

const fetchArrivalSlotsIfRequired =
  (before: FormContent, after: FormContent) =>
  (dispatch: AppThunkDispatch): void => {
    if (after.site && after.date) {
      if (before.site !== after.site || before.date !== after.date) dispatch(fetchArrivalSlots(after.site, after.date));
    } else {
      dispatch(clearArrivalSlots());
    }
  };

const autoSetArrivalSite =
  (state: State, departure: FormContent, forceSameSite: boolean) =>
  (arrival: FormContent): FormContent => {
    const newArrival = { ...arrival };
    const sites = getArrivalSites(state);

    const isArrivalSiteValid = arrival.site && sites.find(s => s.id.toString() === arrival.site) != null;

    if (sites.length === 1) newArrival.site = sites[0].id.toString();
    else if (sites.length > 1) {
      if (isArrivalSiteValid && !forceSameSite) newArrival.site = arrival.site || departure.site;
      else newArrival.site = departure.site;
    }

    return newArrival;
  };

const autoSetCheckpoint =
  (state: State) =>
  (content: FormContent): FormContent => {
    const newContent = { ...content };
    const selectedSite = getSiteWithId(state, content.site);

    if (selectedSite && selectedSite.checkpoints.length === 1) {
      newContent.checkpoint = newContent.checkpoint || selectedSite.checkpoints[0].id.toString();
    }

    return newContent;
  };

export const autoSetArrivalCheckpoint =
  (departure: FormContent) =>
  (arrival: FormContent): FormContent => {
    if (departure.checkpoint && !arrival.checkpoint) {
      return { ...arrival, checkpoint: departure.checkpoint };
    }

    return arrival;
  };

const clearCheckpointIfSiteChanged =
  (before: FormContent) =>
  (after: FormContent): FormContent => {
    if (before.site !== after.site) return { ...after, checkpoint: undefined };
    return after;
  };

export const departureChanged = (departure: FormContent): Action<Action.Type.DEPARTURE_CHANGED> => ({
  type: Action.Type.DEPARTURE_CHANGED,
  departure,
});

export const arrivalChanged = (arrival: FormContent): Action<Action.Type.ARRIVAL_CHANGED> => ({
  type: Action.Type.ARRIVAL_CHANGED,
  arrival,
});

export const departureCheckpointChanged = (
  checkpoint: SiteCheckpointId,
): Action<Action.Type.DEPARTURE_CHECKPOINT_CHANGED> => ({
  type: Action.Type.DEPARTURE_CHECKPOINT_CHANGED,
  checkpoint,
});

export const arrivalCheckpointChanged = (
  checkpoint: SiteCheckpointId,
): Action<Action.Type.ARRIVAL_CHECKPOINT_CHANGED> => ({
  type: Action.Type.ARRIVAL_CHECKPOINT_CHANGED,
  checkpoint,
});

export const invalidArrivalTrip = (): Action<Action.Type.INVALID_ARRIVAL_TRIP> => ({
  type: Action.Type.INVALID_ARRIVAL_TRIP,
});

export const changeDeparture =
  (departure: FormContent, forceSameSite: boolean) =>
  (dispatch: AppThunkDispatch, getState: () => State): void => {
    dispatch(filterArrivalSites(departure.site));

    const state = getState();
    const { departure: existingDeparture, arrival } = state.booking;

    const newDeparture = compose(autoSetCheckpoint(state), clearCheckpointIfSiteChanged(existingDeparture))(departure);

    const currentDate = moment(existingDeparture.date);
    const newDate = moment(newDeparture.date);
    const arrivalDate = moment(arrival.date);

    const hasSiteChanged = existingDeparture.site !== newDeparture.site;
    const hasCheckpointChanged = existingDeparture.checkpoint !== newDeparture.checkpoint;
    const hasDateChanged = !currentDate.isSame(newDate);

    // If the date or the hour has changed, and it's after the current arrival date
    // Then dispatch an event to invalid the arrival trip if there is one
    if (arrivalDate.isBefore(newDate)) dispatch(invalidArrivalTrip());
    else if (arrivalDate.isSame(newDate)) {
      const newHour = moment(newDeparture.hour, 'HH:mm');
      const arrivalHour = moment(arrival.hour, 'HH:mm');
      if (arrivalHour.isBefore(newHour)) dispatch(invalidArrivalTrip());
    }

    // If the site or the date has changed, then clean the tripDetail stored
    if (hasSiteChanged || hasDateChanged) newDeparture.tripDetail = {};

    // Dispatching departure changed in all case
    dispatch(departureChanged(newDeparture));

    // Trying to set arrival site automatically if departure site changed
    if (hasSiteChanged) {
      // Clear checkpoint if site has changed
      const cleared = { ...arrival, checkpoint: undefined };

      const newArrival = compose(
        autoSetArrivalCheckpoint(newDeparture),
        autoSetArrivalSite(state, newDeparture, forceSameSite),
      )(cleared);

      dispatch(arrivalChanged(newArrival));
      dispatch(fetchArrivalSlotsIfRequired(arrival, newArrival));
    } else if (hasCheckpointChanged) {
      const newArrival = compose(autoSetArrivalCheckpoint(newDeparture))(arrival);

      dispatch(arrivalChanged(newArrival));
    }

    dispatch(fetchDepartureSlotsIfRequired(existingDeparture, newDeparture));
    dispatch(estimateBooking(true));
    dispatch(fetchServices());
  };

export const changeArrival =
  (arrival: FormContent) =>
  (dispatch: AppThunkDispatch, getState: () => State): void => {
    const state = getState();
    const { arrival: existingArrival } = state.booking;

    const newArrival = compose(autoSetCheckpoint(state), clearCheckpointIfSiteChanged(existingArrival))(arrival);

    const currentDate = moment(existingArrival.date);
    const newDate = moment(newArrival.date);

    const hasSiteChanged = existingArrival.site !== newArrival.site;
    const hasDateChanged = !currentDate.isSame(newDate);

    // If the site or the date has changed, then clean the tripDetail stored
    if (hasSiteChanged || hasDateChanged) {
      newArrival.tripDetail = {};
      dispatch(clearTripInfos('end'));
    }

    dispatch(arrivalChanged(newArrival));
    dispatch(fetchArrivalSlotsIfRequired(existingArrival, newArrival));
    dispatch(estimateBooking(true));
    dispatch(fetchServices());
  };
